import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Blocks,
  Check,
  Code2,
  FileQuestion,
  Loader2,
  LockKeyhole,
  RefreshCcw,
  SearchCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { trackCtaClick, trackPublicFunnelEvent } from "@/lib/publicFunnel";
import {
  buildProjectIntakeHandoff,
  draftProjectPlan,
  recommendationLabels,
  type ProjectIntakeHandoff,
  type ProjectPlanResult,
  type ProjectStartingPoint,
  type ProjectTranslatorAnswers,
} from "@/lib/projectTranslator";

interface ProjectTranslatorProps {
  onHandoff: (handoff: ProjectIntakeHandoff) => void;
  onRestart?: () => void;
}

const initialAnswers: ProjectTranslatorAnswers = {
  challenge: "",
  startingPoint: "unsure",
  peopleAndWork: "",
  desiredChange: "",
  constraints: "",
};

const startingPointOptions: Array<{
  value: ProjectStartingPoint;
  label: string;
  description: string;
  icon: typeof Code2;
}> = [
  {
    value: "new_app",
    label: "Nothing has been built yet",
    description: "There is a clear need or idea, but no working product.",
    icon: Code2,
  },
  {
    value: "existing_app",
    label: "I have an app or build",
    description: "There is a repo, preview, MVP, no-code build, or live product.",
    icon: Wrench,
  },
  {
    value: "operations",
    label: "The problem is how work moves",
    description: "Requests, decisions, follow-up, or delivery are hard to track.",
    icon: Blocks,
  },
  {
    value: "unsure",
    label: "I am still sorting that out",
    description: "The pain is clear, but the right kind of help is not.",
    icon: FileQuestion,
  },
];

const steps = ["The problem", "What exists", "The people", "The outcome"];

const ProjectTranslator = ({ onHandoff, onRestart }: ProjectTranslatorProps) => {
  const [answers, setAnswers] = useState<ProjectTranslatorAnswers>(initialAnswers);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [isDrafting, setIsDrafting] = useState(false);
  const [result, setResult] = useState<ProjectPlanResult | null>(null);

  const updateAnswer = <Key extends keyof ProjectTranslatorAnswers>(
    key: Key,
    value: ProjectTranslatorAnswers[Key]
  ) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setError("");
  };

  const validateStep = () => {
    if (step === 0 && answers.challenge.trim().length < 20) {
      setError("Give us a little more detail so the draft has a real problem to work from.");
      return false;
    }
    if (step === 2 && answers.peopleAndWork.trim().length < 10) {
      setError("Tell us who is involved and what they are trying to get done.");
      return false;
    }
    if (step === 3 && answers.desiredChange.trim().length < 10) {
      setError("Tell us what should be easier or possible when this works.");
      return false;
    }
    return true;
  };

  const moveToStep = (nextStep: number) => {
    setStep(Math.max(0, Math.min(nextStep, steps.length - 1)));
    setError("");
    window.setTimeout(() => {
      document.getElementById("project-translator-form")?.scrollIntoView({ block: "start" });
    }, 0);
  };

  const continueForward = () => {
    if (!validateStep()) return;
    if (step === 0) {
      trackPublicFunnelEvent("project_translator_started", {
        service: "project_translator",
        step: step + 1,
        step_name: steps[step],
      });
    }
    trackPublicFunnelEvent("project_translator_step_completed", {
      service: "project_translator",
      step: step + 1,
      step_name: steps[step],
      starting_point: step === 1 ? answers.startingPoint : undefined,
    });
    moveToStep(step + 1);
  };

  const draftPlan = async () => {
    if (!validateStep()) return;
    setIsDrafting(true);
    setError("");

    try {
      const nextResult = await draftProjectPlan(answers);
      setResult(nextResult);
      trackPublicFunnelEvent("project_translator_completed", {
        service: "project_translator",
        mode: nextResult.mode,
        recommendation: nextResult.plan.recommendation,
      });
    } catch {
      trackPublicFunnelEvent("project_translator_failed", {
        service: "project_translator",
        reason: "draft_error",
      });
      setError("We could not prepare the draft. Your answers are still here, so you can try again.");
    } finally {
      setIsDrafting(false);
    }
  };

  const usePlan = () => {
    if (!result) return;
    onHandoff(buildProjectIntakeHandoff(answers, result));
    trackCtaClick({
      label: "Use This Plan in the Intake",
      destination: "/project-planner#project-intake",
      placement: "project_translator_result",
      service: "project_translator",
    });
    trackPublicFunnelEvent("project_translator_plan_used", {
      service: "project_translator",
      mode: result.mode,
      recommendation: result.plan.recommendation,
    });
    window.setTimeout(() => {
      document.getElementById("project-intake")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  if (result) {
    const { plan } = result;
    const RecommendationIcon = plan.recommendation === "APP_BUILD"
      ? Code2
      : plan.recommendation === "APP_RESCUE"
        ? Wrench
        : plan.recommendation === "SVM_OPERATOR"
          ? Blocks
          : SearchCheck;

    return (
      <section aria-labelledby="project-plan-heading" className="border-y border-slate-300 bg-[#f3eee4] py-14 md:py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mb-8 flex flex-col gap-4 border-b border-slate-300 pb-8 md:flex-row md:items-end md:justify-between">
            <div className="max-w-3xl">
              <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase text-rose-700">
                <Sparkles className="h-4 w-4" />
                {result.mode === "ai_assisted" ? "AI-assisted project draft" : "Guided project draft"}
              </p>
              <h2 id="project-plan-heading" className="text-4xl md:text-5xl">Here is what we heard.</h2>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setResult(null);
                onRestart?.();
              }}
              className="w-full gap-2 rounded-md md:w-auto"
            >
              <RefreshCcw className="h-4 w-4" />
              Change my answers
            </Button>
          </div>

          <div className="grid gap-10 lg:grid-cols-[0.84fr_1.16fr]">
            <div>
              <p className="mb-3 text-xs font-bold uppercase text-slate-500">The problem</p>
              <p className="text-xl font-semibold leading-8 text-slate-900">{plan.problemHeard}</p>

              <div className="mt-8 border-y border-slate-300 py-6">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-950 text-white">
                    <RecommendationIcon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase text-rose-700">Likely starting path</p>
                    <h3 className="text-2xl">{recommendationLabels[plan.recommendation]}</h3>
                  </div>
                </div>
                <p className="font-medium text-slate-700">{plan.recommendationReason}</p>
              </div>

              <div className="mt-6 border-l-4 border-amber-300 pl-4 text-sm font-medium text-slate-700">
                <p>{result.notice}</p>
                <p className="mt-2">This is not a quote, final scope, architecture, or delivery promise.</p>
              </div>
            </div>

            <div>
              <div className="border-t border-slate-300">
                <h3 className="border-b border-slate-300 py-4 text-2xl">A sensible first phase</h3>
                {plan.firstPhase.map((item, index) => (
                  <div key={item} className="grid grid-cols-[42px_1fr] gap-3 border-b border-slate-300 py-4">
                    <span className="font-mono text-xs font-bold text-rose-700">0{index + 1}</span>
                    <p className="font-medium text-slate-800">{item}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid gap-8 md:grid-cols-2">
                <div>
                  <h3 className="mb-3 text-xl">What we still need to learn</h3>
                  <ul className="grid gap-3">
                    {plan.unknowns.map((item) => (
                      <li key={item} className="flex gap-2 text-sm font-medium text-slate-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="mb-3 text-xl">The next conversation</h3>
                  <p className="text-sm font-medium text-slate-700">{plan.nextConversation}</p>
                </div>
              </div>

              <Button type="button" size="lg" onClick={usePlan} className="mt-8 w-full gap-2 rounded-md bg-slate-950 font-bold text-white hover:bg-rose-700 sm:w-auto">
                Use This Plan in the Intake
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="project-translator-heading" className="border-y border-slate-300 bg-[#f3eee4] py-8 md:py-14">
      <div className="container mx-auto grid gap-8 px-4 lg:grid-cols-[0.68fr_1.32fr] lg:gap-12 lg:px-8">
        <div className="order-2 lg:order-1">
          <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase text-rose-700">
            <Sparkles className="h-4 w-4" />
            SVM AI Capacity Snapshot
          </p>
          <h2 id="project-translator-heading" className="text-4xl md:text-5xl">Start with what your organization already knows.</h2>
          <p className="mt-5 max-w-xl font-medium text-slate-700">
            You do not need to know whether the answer is AI, an app, automation, or a better process. Answer four questions in your own words. We will turn them into a practical first draft.
          </p>
          <div className="mt-8 border-y border-slate-300 py-5 text-sm font-medium text-slate-700">
            <div className="flex gap-3">
              <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
              <p>
                Do not share passwords, credentials, private customer records, health information, case files, or anything you are not allowed to disclose.
              </p>
            </div>
          </div>
          <Link to="/contact" className="mt-5 inline-flex text-sm font-bold text-slate-900 underline underline-offset-4 hover:text-rose-700">
            I already know what I need. Show me the regular form.
          </Link>
        </div>

        <div id="project-translator-form" className="order-1 scroll-mt-24 border border-slate-300 bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] md:p-8 lg:order-2">
          <div className="mb-6 flex gap-3 border-b border-slate-200 pb-5 text-sm font-medium leading-6 text-slate-600 lg:hidden">
            <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
            <p>Four short questions in plain language. Do not include passwords, private records, or regulated data.</p>
          </div>
          <div className="mb-8">
            <div className="mb-3 flex items-center justify-between text-xs font-bold uppercase text-slate-500">
              <span>Question {step + 1} of {steps.length}</span>
              <span>{steps[step]}</span>
            </div>
            <div className="grid grid-cols-4 gap-2" aria-label={`Step ${step + 1} of ${steps.length}: ${steps[step]}`} role="progressbar" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={step + 1}>
              {steps.map((label, index) => (
                <span key={label} aria-hidden="true" className={`h-1 ${index <= step ? "bg-rose-700" : "bg-slate-200"}`} />
              ))}
            </div>
          </div>

          {step === 0 && (
            <div>
              <label htmlFor="translator-challenge" className="text-2xl font-bold text-slate-950">
                What is frustrating, slow, or stuck?
              </label>
              <p id="translator-challenge-help" className="mt-2 text-sm font-medium text-slate-600">
                Describe what happens in the business. You do not need to name a tool or diagnose the cause.
              </p>
              <Textarea
                id="translator-challenge"
                aria-describedby="translator-challenge-help"
                value={answers.challenge}
                onChange={(event) => updateAnswer("challenge", event.target.value)}
                placeholder="For example: New requests arrive by email and text, nobody knows who followed up, and good leads sometimes go cold."
                className="mt-5 min-h-40 resize-y"
                autoFocus
              />
            </div>
          )}

          {step === 1 && (
            <fieldset>
              <legend className="text-2xl font-bold text-slate-950">What already exists?</legend>
              <p className="mt-2 text-sm font-medium text-slate-600">Choose the closest answer. It does not have to be perfect.</p>
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {startingPointOptions.map((option) => {
                  const Icon = option.icon;
                  const selected = answers.startingPoint === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`cursor-pointer border p-4 transition focus-within:ring-2 focus-within:ring-rose-600 focus-within:ring-offset-2 ${
                        selected ? "border-slate-950 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-950 hover:border-slate-500"
                      }`}
                    >
                      <input
                        type="radio"
                        name="starting-point"
                        value={option.value}
                        checked={selected}
                        onChange={() => {
                          updateAnswer("startingPoint", option.value);
                          trackPublicFunnelEvent("project_translator_starting_point_selected", {
                            service: "project_translator",
                            starting_point: option.value,
                          });
                        }}
                        className="sr-only"
                      />
                      <Icon className={`mb-4 h-5 w-5 ${selected ? "text-amber-300" : "text-rose-700"}`} />
                      <span className="block font-bold">{option.label}</span>
                      <span className={`mt-1 block text-sm font-medium ${selected ? "text-slate-300" : "text-slate-600"}`}>
                        {option.description}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}

          {step === 2 && (
            <div>
              <label htmlFor="translator-people" className="text-2xl font-bold text-slate-950">
                Who is involved, and what are they trying to get done?
              </label>
              <p id="translator-people-help" className="mt-2 text-sm font-medium text-slate-600">
                Name the people or roles and the job they need to complete. Customers, staff, partners, and managers can all be part of the answer.
              </p>
              <Textarea
                id="translator-people"
                aria-describedby="translator-people-help"
                value={answers.peopleAndWork}
                onChange={(event) => updateAnswer("peopleAndWork", event.target.value)}
                placeholder="For example: Clients send requests, two staff members review them, and I approve the final response."
                className="mt-5 min-h-40 resize-y"
                autoFocus
              />
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-6">
              <div>
                <label htmlFor="translator-outcome" className="text-2xl font-bold text-slate-950">
                  What should be easier or possible when this works?
                </label>
                <p id="translator-outcome-help" className="mt-2 text-sm font-medium text-slate-600">
                  Describe the change you want to see, not a list of features.
                </p>
                <Textarea
                  id="translator-outcome"
                  aria-describedby="translator-outcome-help"
                  value={answers.desiredChange}
                  onChange={(event) => updateAnswer("desiredChange", event.target.value)}
                  placeholder="For example: Every request has an owner, the next step is visible, and clients get an answer on time."
                  className="mt-4 min-h-32 resize-y"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="translator-constraints" className="text-sm font-bold text-slate-950">
                  Anything we should know about timing, existing tools, or limits? <span className="font-medium text-slate-500">Optional</span>
                </label>
                <p id="translator-constraints-help" className="mt-1 text-sm font-medium text-slate-600">
                  Plain language is enough. "We must keep HubSpot" or "we need a first test this quarter" is useful.
                </p>
                <Textarea
                  id="translator-constraints"
                  aria-describedby="translator-constraints-help"
                  value={answers.constraints}
                  onChange={(event) => updateAnswer("constraints", event.target.value)}
                  placeholder="Share only what is useful and safe to discuss."
                  className="mt-3 min-h-24 resize-y"
                />
              </div>
              <div className="border-l-4 border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-950">
                Your answers may be sent to SVM's AI service to prepare this draft. Do not include private or regulated data. A person reviews every plan before SVM recommends work.
              </div>
            </div>
          )}

          {error && <p role="alert" className="mt-5 border-l-4 border-red-600 bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{error}</p>}

          <div className="sticky bottom-0 z-10 -mx-5 mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white/95 px-5 pb-1 pt-5 backdrop-blur sm:static sm:mx-0 sm:flex-row sm:justify-between sm:bg-transparent sm:px-0 sm:pb-0">
            <Button
              type="button"
              variant="ghost"
              disabled={step === 0 || isDrafting}
              onClick={() => moveToStep(step - 1)}
              className="gap-2 rounded-md"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            {step < steps.length - 1 ? (
              <Button type="button" onClick={continueForward} className="gap-2 rounded-md bg-slate-950 text-white hover:bg-rose-700">
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button type="button" disabled={isDrafting} onClick={draftPlan} className="gap-2 rounded-md bg-slate-950 text-white hover:bg-rose-700">
                {isDrafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {isDrafting ? "Drafting your plan..." : "Draft My Plan"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectTranslator;
